import type { AuthResponse } from '@/lib/types/auth.types';
import type { User } from '@/lib/types/user.types';
import { transformBackendUser } from '@/lib/utils/userTransformers';
import apiClient from './client';

interface LoginCredentials {
  email: string;
  password: string;
}

interface PhoneLoginCredentials {
  phone: string;
  password: string;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  userType: 'client' | 'supplier' | 'admin';
  phone?: string;
  companyName?: string;
  commercialRegister?: string;
  /** How user verified during OTP flow: 'email' | 'phone' - marks corresponding verified flag in DB */
  verifiedVia?: 'email' | 'phone';
}

// Backend response interface (snake_case)
interface BackendAuthResponse {
  user?: any; // Backend returns user in snake_case format, will be transformed to User
  token?: string;
  message?: string;
  error?: string;
}

// Helper to transform backend response to frontend format
const transformAuthResponse = (backendResponse: BackendAuthResponse): AuthResponse => {
  // Validate response structure
  if (!backendResponse) {
    console.error('❌ transformAuthResponse: No response provided');
    throw new Error('AUTH_INVALID_RESPONSE');
  }

  // Log the response for debugging
  console.log('🔍 transformAuthResponse input:', JSON.stringify(backendResponse, null, 2));

  if (!backendResponse.user) {
    console.error('❌ transformAuthResponse: User data missing', {
      response: backendResponse,
      hasToken: !!backendResponse.token,
      hasMessage: !!backendResponse.message,
      hasError: !!backendResponse.error,
    });
    throw new Error('AUTH_USER_DATA_MISSING');
  }

  if (!backendResponse.token) {
    console.error('❌ transformAuthResponse: Token missing', backendResponse);
    throw new Error('AUTH_TOKEN_MISSING');
  }

  // Transform backend user data (snake_case) to full User type
  const user = transformBackendUser(backendResponse.user);

  return {
    user,
    token: backendResponse.token,
  };
};

export const loginUser = async (credentials: LoginCredentials): Promise<AuthResponse> => {
  try {
    const response = await apiClient.post<BackendAuthResponse>('/v1/auth/login', credentials);

    // Debug: Log the response to see what we're getting
    console.log('🔍 Login Response:', JSON.stringify(response.data, null, 2));

    if (!response || !response.data) {
      console.error('❌ No response data received');
      throw new Error('AUTH_INVALID_RESPONSE');
    }

    // Check if user exists in response before transforming
    if (!response.data.user) {
      console.error('❌ User data missing in response:', {
        hasData: !!response.data,
        hasUser: !!response.data.user,
        hasToken: !!response.data.token,
        responseKeys: Object.keys(response.data),
        fullResponse: response.data,
      });
      throw new Error('AUTH_USER_DATA_MISSING');
    }

    return transformAuthResponse(response.data);
  } catch (error: any) {
    // Log the error for debugging
    console.error('❌ Login Error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      isNetworkError: !error.response,
    });

    // Extract error message from backend response
    // Backend returns errors in format: { "error": "message" }
    if (error.response?.data) {
      // Check for error field first (backend standard format)
      if (error.response.data.error) {
        const errorMessage =
          typeof error.response.data.error === 'string'
            ? error.response.data.error
            : String(error.response.data.error);
        if (errorMessage.trim()) {
          throw new Error(errorMessage);
        }
      }
      // Check for message field as fallback
      if (error.response.data.message) {
        const errorMessage =
          typeof error.response.data.message === 'string'
            ? error.response.data.message
            : String(error.response.data.message);
        if (errorMessage.trim()) {
          throw new Error(errorMessage);
        }
      }
    }

    // Handle network errors (no response from server)
    if (!error.response) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('AUTH_LOGIN_TIMEOUT');
      }
      if (error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        throw new Error('AUTH_LOGIN_NETWORK_ERROR');
      }
      throw new Error('AUTH_LOGIN_CONNECTION_ERROR');
    }

    // If it's already our custom error with a message, re-throw it
    if (error.message && error.message.trim()) {
      throw error;
    }

    // Fallback: use status code to determine error message
    const status = error.response?.status;
    if (status === 401 || status === 403) {
      throw new Error('AUTH_INVALID_CREDENTIALS');
    }
    if (status === 400) {
      throw new Error('AUTH_INVALID_INPUT');
    }
    if (status >= 500) {
      throw new Error('AUTH_SERVER_ERROR');
    }

    // Final fallback
    throw new Error('AUTH_LOGIN_ERROR');
  }
};

export const loginUserByPhone = async (
  credentials: PhoneLoginCredentials
): Promise<AuthResponse> => {
  try {
    const response = await apiClient.post<BackendAuthResponse>('/v1/auth/login-phone', {
      phone: credentials.phone,
      password: credentials.password,
    });

    // Reuse the same validation and transformation logic
    if (!response || !response.data) {
      console.error('❌ No response data received (phone login)');
      throw new Error('AUTH_INVALID_RESPONSE');
    }

    if (!response.data.user || !response.data.token) {
      console.error('❌ Invalid phone login response structure:', response.data);
      throw new Error('AUTH_INVALID_RESPONSE');
    }

    return transformAuthResponse(response.data);
  } catch (error: any) {
    console.error('❌ Phone Login Error:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });

    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const registerUser = async (data: RegisterData): Promise<AuthResponse> => {
  try {
    const requestBody: any = {
      full_name: data.name,
      email: data.email,
      password: data.password,
      user_type: data.userType || 'client', // Ensure user_type is always set
      phone: data.phone || '',
    };

    // Add optional fields if provided
    if (data.companyName) {
      requestBody.company_name = data.companyName;
    }
    if (data.commercialRegister) {
      requestBody.commercial_register = data.commercialRegister;
    }
    if (data.verifiedVia) {
      requestBody.verified_via = data.verifiedVia;
    }

    // Debug: Log the request body
    console.log('🔍 Register Request:', {
      user_type: requestBody.user_type,
      email: requestBody.email,
      hasCompanyName: !!requestBody.company_name,
    });

    const response = await apiClient.post<BackendAuthResponse>('/v1/auth/register', requestBody);
    if (!response.data) {
      throw new Error('AUTH_INVALID_RESPONSE');
    }
    return transformAuthResponse(response.data);
  } catch (error: any) {
    // Extract error message from backend response
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const logoutUser = async (): Promise<void> => {
  try {
    await apiClient.post('/v1/auth/logout');
  } catch (error: any) {
    // Log the error but don't throw - we still want to clear local state
    console.warn('⚠️ Logout API call failed, but continuing with local logout:', error);
    // Don't throw - allow logout to proceed even if API call fails
  }
};

export const refreshToken = async (): Promise<{ token: string }> => {
  const response = await apiClient.post<{ token: string }>('/v1/auth/refresh');
  return response.data;
};

// Helper function to get user ID from email (for forgot password)
const getUserIdByEmail = async (email: string): Promise<string> => {
  try {
    // Try to get user info - this might require a public endpoint
    // For now, we'll need to handle this differently
    // If user is logged in, we can get user_id from /auth/me
    const user = await getCurrentUser();
    return user.id;
  } catch (error) {
    // If user is not logged in, we need a different approach
    // For now, throw an error asking for user_id
    throw new Error('AUTH_LOGIN_REQUIRED');
  }
};

export const verifyEmail = async (
  email: string,
  otpCode: string,
  userId?: string
): Promise<void> => {
  try {
    // If userId is not provided, try to get it from current user
    let user_id = userId;
    if (!user_id) {
      try {
        const user = await getCurrentUser();
        user_id = user.id;
      } catch (error) {
        throw new Error('AUTH_EMAIL_VERIFICATION_REQUIRED');
      }
    }

    await apiClient.post('/v1/otp/verify', {
      user_id: user_id,
      otp_code: otpCode,
      purpose: 'email_verification',
    });
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const verifyPhone = async (
  phone: string,
  otpCode: string,
  userId?: string
): Promise<void> => {
  try {
    // If userId is not provided, try to get it from current user
    let user_id = userId;
    if (!user_id) {
      try {
        const user = await getCurrentUser();
        user_id = user.id;
      } catch (error) {
        throw new Error('AUTH_PHONE_VERIFICATION_REQUIRED');
      }
    }

    await apiClient.post('/v1/otp/verify', {
      user_id: user_id,
      otp_code: otpCode,
      purpose: 'phone_verification',
    });
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const forgotPassword = async (email: string): Promise<void> => {
  try {
    await apiClient.post('/v1/auth/forgot-password', { email });
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const sendPasswordResetOTP = async (email: string, userId: string): Promise<void> => {
  try {
    await apiClient.post('/v1/otp/send', {
      user_id: userId,
      purpose: 'password_reset',
      channel: 'email',
    });
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const resetPassword = async (
  token: string,
  newPassword: string
): Promise<void> => {
  try {
    await apiClient.post('/v1/auth/reset-password', {
      token: token,
      new_password: newPassword,
    });
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const getCurrentUser = async (): Promise<User> => {
  const response = await apiClient.get<any>('/v1/auth/me');
  // Transform backend user data (snake_case) to full User type
  return transformBackendUser(response.data);
};

export const resendVerification = async (
  type: 'email' | 'phone',
  userId?: string,
  email?: string,
  phone?: string
): Promise<void> => {
  try {
    // If userId is not provided, try to get it from current user
    let user_id = userId;
    if (!user_id) {
      try {
        const user = await getCurrentUser();
        user_id = user.id;
      } catch (error) {
        throw new Error('AUTH_RESEND_VERIFICATION_REQUIRED');
      }
    }

    const purpose = type === 'email' ? 'email_verification' : 'phone_verification';
    // Default to whatsapp for phone verification (can be changed to 'sms' if needed)
    const channel = type === 'email' ? 'email' : 'whatsapp';

    await apiClient.post('/v1/otp/resend', {
      user_id: user_id,
      purpose: purpose,
      channel: channel,
    });
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const logoutAll = async (): Promise<void> => {
  await apiClient.post('/v1/auth/logout-all');
};

// Two-Factor Authentication
// ⚠️ NOTE: 2FA endpoints are not implemented in Backend yet
// These functions are kept for future implementation
// TODO: Implement 2FA endpoints in Backend or remove these functions

export const enable2FA = async (): Promise<{ qrCode: string; secret: string }> => {
  throw new Error('AUTH_2FA_NOT_AVAILABLE');
  // const response = await apiClient.post<{ qrCode: string; secret: string }>('/v1/auth/2fa/enable');
  // return response.data;
};

export const disable2FA = async (password: string): Promise<void> => {
  throw new Error('AUTH_2FA_NOT_AVAILABLE');
  // await apiClient.post('/v1/auth/2fa/disable', { password });
};

export const verify2FA = async (code: string): Promise<{ verified: boolean }> => {
  throw new Error('AUTH_2FA_NOT_AVAILABLE');
  // const response = await apiClient.post<{ verified: boolean }>('/v1/auth/2fa/verify', { code });
  // return response.data;
};

export const getBackupCodes = async (): Promise<{ codes: string[] }> => {
  throw new Error('AUTH_2FA_NOT_AVAILABLE');
  // const response = await apiClient.get<{ codes: string[] }>('/v1/auth/2fa/backup-codes');
  // return response.data;
};

// Pre-registration OTP response
interface SendRegistrationOTPResponse {
  message: string;
  expires_at: string;
  otp_code?: string; // Only in development mode
}

type RegistrationOtpMethod = 'phone' | 'email';

// Pre-registration OTP - Send OTP before registration (phone via WhatsApp, or email)
export const sendRegistrationOTP = async (input: {
  method: RegistrationOtpMethod;
  phone?: string;
  email?: string;
}): Promise<{ otpCode?: string }> => {
  try {
    const payload =
      input.method === 'email'
        ? { email: input.email, channel: 'email' }
        : { phone: input.phone, channel: 'whatsapp' };

    const response = await apiClient.post<SendRegistrationOTPResponse>(
      '/v1/otp/send-registration',
      payload
    );

    // Return OTP code if available (development mode)
    return { otpCode: response.data.otp_code };
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

// Pre-registration OTP - Verify OTP before registration (phone or email)
export const verifyRegistrationOTP = async (
  input: { method: RegistrationOtpMethod; phone?: string; email?: string; otpCode: string }
): Promise<{ verified: boolean }> => {
  try {
    const response = await apiClient.post<{ verified: boolean }>('/v1/otp/verify-registration', {
      phone: input.method === 'phone' ? input.phone : undefined,
      email: input.method === 'email' ? input.email : undefined,
      otp_code: input.otpCode,
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

// Google OAuth Authentication
// Receives an ID token from Google Identity Services and sends it to backend
// for verification and user registration/login
// intent: 'login' = from login page (reject new users); 'register' = from register page (create with userType)
interface GoogleAuthInput {
  idToken: string;
  userType: 'client' | 'supplier';
  intent?: 'login' | 'register';
  locale?: string;
}

export const googleAuth = async (input: GoogleAuthInput): Promise<AuthResponse> => {
  try {
    console.log('🔍 Sending Google auth request...');
    const response = await apiClient.post<BackendAuthResponse>('/v1/auth/google', {
      id_token: input.idToken,
      user_type: input.userType,
      intent: input.intent ?? 'register',
      locale: input.locale,
    });

    // Debug: Log the response
    console.log('🔍 Google Auth Response:', JSON.stringify(response.data, null, 2));

    if (!response || !response.data) {
      console.error('❌ No response data received from Google auth');
      throw new Error('AUTH_INVALID_RESPONSE');
    }

    if (!response.data.user || !response.data.token) {
      console.error('❌ Invalid Google auth response structure:', response.data);
      throw new Error('AUTH_INVALID_RESPONSE');
    }

    return transformAuthResponse(response.data);
  } catch (error: any) {
    console.error('❌ Google Auth Error:', error);

    // Handle network errors (no response from server)
    if (!error.response) {
      // Check for timeout
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.error('⏱️ Google auth request timed out');
        throw new Error('AUTH_GOOGLE_TIMEOUT');
      }
      // Check for network error
      if (error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        console.error('🌐 Network error during Google auth');
        throw new Error('AUTH_GOOGLE_NETWORK_ERROR');
      }
      // Connection refused or other connection issues
      throw new Error('AUTH_GOOGLE_CONNECTION_ERROR');
    }

    // Extract error message from backend response
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    // Handle specific error codes (backend returns error in error.response.data.error)
    const status = error.response?.status;
    if (status === 401) {
      throw new Error('AUTH_GOOGLE_TOKEN_INVALID');
    }
    if (status === 403) {
      throw new Error('AUTH_ACCOUNT_SUSPENDED');
    }
    if (status === 409) {
      throw new Error('AUTH_EMAIL_ALREADY_EXISTS');
    }
    if (status === 503) {
      throw new Error('AUTH_GOOGLE_NOT_CONFIGURED');
    }
    if (status === 504 || status === 408) {
      throw new Error('AUTH_GOOGLE_TIMEOUT');
    }

    throw error;
  }
};
